import { ChangeEvent, FC, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
    Button,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Modal,
    FieldErrorList
} from 'common/components';
import { ButtonVariant } from 'common/enums';
import { FieldError } from 'common/models';
import { ACTION_ITEMS } from 'common/config';
import { addValidationError, removeValidationError } from 'common/utils/error';
import { validateMaxLength, validateYearMonthForCompanyFootprint } from 'common/utils/validator';

import CompanyFootprintForm from '../company-footprint-form/CompanyFootprintForm';
import { CompanyInfoDTO } from 'modules/data-management/models/data-management.model';
import { CompanyInfoField } from 'modules/data-management/enums/data-management.enum';
import {
    DESCRIPTION_EMPTY_ERROR,
    DESCRIPTION_LENGTH,
    DESCRIPTION_MAX_LENGTH_ERROR,
    EDIT_EY_FOOTPRINT_INFO,
    SOURCE_NAME_EMPTY_ERROR,
    SOURCE_NAME_MAX_LENGTH,
    SOURCE_NAME_MAX_LENGTH_ERROR,
    YEAR_EMPTY_ERROR,
    YEAR_SHORT_DATE_FORMAT_ERROR
} from 'modules/data-management/config';
import {
    getEYCompanyFootprintInfo,
    handleEYCompanyFootprintInfoUpdate
} from 'modules/data-management/utils/company-footprint.utils';

import './CompanyFootprintAddEdit.scss';

type CompanyFootprintAddEditProps = {
    onClose: () => void;
    isVisible: boolean;
};

const CompanyFootprintAddEdit: FC<CompanyFootprintAddEditProps> = ({ onClose, isVisible }) => {
    const [source, setSource] = useState<string>('');
    const [year, setYear] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [fieldErrors, setFieldErrors] = useState<Array<FieldError>>([]);
    const companyInfoResponse = useRef<null | CompanyInfoDTO>(null);
    const [disabled, setDisabled] = useState<boolean>(true);
    const dispatch = useDispatch();

    const removeInitialDisabled = () => {
        if (disabled) setDisabled(false);
    };
    const handleSourceNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        removeInitialDisabled();
        let errors = removeValidationError(fieldErrors, CompanyInfoField.Source);
        if (event.target.value.trim().length === 0) {
            errors = addValidationError(errors, CompanyInfoField.Source, SOURCE_NAME_EMPTY_ERROR);
        } else if (validateMaxLength(event.target.value, SOURCE_NAME_MAX_LENGTH)) {
            errors = addValidationError(
                errors,
                CompanyInfoField.Source,
                SOURCE_NAME_MAX_LENGTH_ERROR
            );
        }
        setSource(event.target.value);
        setFieldErrors(errors);
    };

    const handleDescriptionChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
        removeInitialDisabled();
        let errors = removeValidationError(fieldErrors, CompanyInfoField.Description);
        if (event.target.value.trim().length === 0) {
            errors = addValidationError(
                errors,
                CompanyInfoField.Description,
                DESCRIPTION_EMPTY_ERROR
            );
        }
        if (validateMaxLength(event.target.value, DESCRIPTION_LENGTH)) {
            errors = addValidationError(
                errors,
                CompanyInfoField.Description,
                DESCRIPTION_MAX_LENGTH_ERROR
            );
        }
        setDescription(event.target.value);
        setFieldErrors(errors);
    };

    const handleYearChange = (value: Date) => {
        removeInitialDisabled();
        let errors = removeValidationError(fieldErrors, CompanyInfoField.Year);
        if (value == null || value == undefined) {
            errors = addValidationError(errors, CompanyInfoField.Year, YEAR_EMPTY_ERROR);
        }
        const year = value && value.getFullYear().toString().substring(2); // Get the last two digits of the year
        const monthIndex = value && value.getMonth(); // Get the month index

        const monthNames = [
            'Jan',
            'Feb',
            'Mar',
            'Apr',
            'May',
            'Jun',
            'Jul',
            'Aug',
            'Sep',
            'Oct',
            'Nov',
            'Dec'
        ];
        const monthAbbreviation = monthNames[monthIndex]; // Get the month abbreviation

        const formattedDate = year + '-' + monthAbbreviation;
        const regex = /^\d{2}-[A-Za-z]{3}$/;
        if (regex.test(formattedDate)) {
            setYear(formattedDate || '');
        } else {
            errors = addValidationError(
                errors,
                CompanyInfoField.Year,
                YEAR_SHORT_DATE_FORMAT_ERROR
            );
        }
        setFieldErrors(errors);
    };

    const handleUpdate = () => {
        if (source && year && description && companyInfoResponse.current) {
            handleEYCompanyFootprintInfoUpdate(
                fieldErrors,
                setFieldErrors,
                { source, year, description, companyId: companyInfoResponse.current?.companyId },
                dispatch,
                onClose
            );
        }
    };

    useEffect(() => {
        getEYCompanyFootprintInfo(
            fieldErrors,
            setFieldErrors,
            setSource,
            setYear,
            setDescription,
            companyInfoResponse,
            dispatch
        );
        return () => {
            setSource('');
            setYear('');
            setDescription('');
            companyInfoResponse.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Modal
            className="company-footprint-edit"
            show={isVisible}
            style={{ width: '51vw' }}
            onClose={onClose}>
            <ModalHeader className="company-footprint-edit-header">
                <header className="company-footprint-edit-header-name-wrapper">
                    {EDIT_EY_FOOTPRINT_INFO}
                </header>
            </ModalHeader>
            <ModalBody className="company-footprint-edit-body">
                <FieldErrorList field={CompanyInfoField.Modal} errors={fieldErrors} />
                <CompanyFootprintForm
                    fieldErrors={fieldErrors}
                    source={source}
                    year={year}
                    description={description}
                    handleSourceNameChange={handleSourceNameChange}
                    handleDescriptionChange={handleDescriptionChange}
                    handleYearChange={handleYearChange}
                />
            </ModalBody>
            <ModalFooter className="company-footprint-edit-footer">
                <Button
                    variant={ButtonVariant.Primary}
                    disabled={fieldErrors.length > 0 || disabled}
                    onClick={handleUpdate}>
                    {ACTION_ITEMS.SAVE_BUTTON}
                </Button>
                <Button variant={ButtonVariant.Secondary} onClick={onClose}>
                    {ACTION_ITEMS.CANCEL_BUTTON}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default CompanyFootprintAddEdit;
